import React, { forwardRef } from 'react';

/**
 * Checkbox Component
 * A reusable checkbox with label and dark mode support
 */
const Checkbox = forwardRef(({
  label,
  error,
  className = '',
  containerClassName = '',
  disabled = false,
  checked,
  onChange,
  ...props
}, ref) => {
  return (
    <div className={`mb-4 ${containerClassName}`}>
      <label className={`inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={`
            w-4 h-4 
            text-blue-600 
            bg-white dark:bg-gray-800
            border-gray-300 dark:border-gray-600
            rounded
            focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
            ${className}
          `}
          {...props}
        />
        {label && (
          <span className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50' : ''}`}>
            {label}
          </span>
        )}
      </label>
      {error && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

/**
 * Radio Component
 * A reusable radio button with label and dark mode support
 */
export const Radio = forwardRef(({
  label,
  error,
  className = '',
  containerClassName = '',
  disabled = false,
  ...props
}, ref) => {
  return (
    <div className={containerClassName}>
      <label className={`inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          ref={ref}
          type="radio"
          disabled={disabled}
          className={`
            w-4 h-4 
            text-blue-600 
            bg-white dark:bg-gray-800
            border-gray-300 dark:border-gray-600
            focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
            ${className}
          `}
          {...props}
        />
        {label && (
          <span className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50' : ''}`}>
            {label}
          </span>
        )}
      </label>
      {error && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
});

Radio.displayName = 'Radio';

/**
 * RadioGroup Component
 * A group of radio buttons
 */
export const RadioGroup = ({
  label,
  options = [],
  value,
  onChange,
  name,
  error,
  className = '',
  horizontal = false,
}) => {
  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </span>
      )}
      <div className={`flex ${horizontal ? 'flex-row gap-4' : 'flex-col gap-2'}`}>
        {options.map((option) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          
          return (
            <Radio
              key={optionValue}
              name={name}
              value={optionValue}
              label={optionLabel}
              checked={value === optionValue}
              onChange={() => onChange?.(optionValue)}
            />
          );
        })}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default Checkbox;

